import { schema } from '@edgevanta/db/drizzle/client';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod/v4';
import { createQuerySchemaWithPagination } from '../../shared';
import { bidSizeRangesSchema } from '../../shared/bid-size-schema';
import {
  ITEM_TYPE_ROUGH_IN,
  ITEM_TYPE_PREDICT,
  TARGET_MARKET,
  TARGET_MY_BID,
  TARGET_ROUGH_IN,
  TARGET_VENDOR,
} from './forecasts.types';

export const bidFiltersSchema = z.preprocess(
  (val) => {
    if (val && typeof val === 'object' && 'vendorId' in val) {
      const { vendorId, ...rest } = val as Record<string, unknown>;
      if (typeof vendorId === 'string' && vendorId) {
        return { ...rest, vendorIds: [vendorId] };
      }
      return rest;
    }
    return val;
  },
  z.object({
    stateIds: z.array(z.string().uuid()).nullish(),
    counties: z.string().nullish(),
    districts: z.string().nullish(),
    ownerName: z.string().nullish(),
    dateFrom: z.string().nullish(),
    dateTo: z.string().nullish(),
    minBidders: z.number().nullish(),
    maxBidders: z.number().nullish(),
    bidSizes: bidSizeRangesSchema.nullish(),
    ranks: z.string().nullish(),
    vendorIds: z.array(z.string()).nullish(),
    globalVendorIds: z.array(z.string().uuid()).nullish(),
    vendorName: z.string().nullish(),
    bidExternalIds: z.array(z.string()).nullish(),
    bidIds: z.array(z.string().uuid()).nullish(),
    qtyLowerBound: z.number().min(0).nullish(),
    qtyUpperBound: z.number().min(0).nullish(),
    lsBandLower: z.number().min(0).nullish(),
    lsBandUpper: z.number().min(0).nullish(),
    businessUnitIds: z.array(z.string().uuid()).nullish(),
    workTypeIds: z.array(z.string().uuid()).nullish(),
    estimatorIds: z.array(z.string().uuid()).nullish(),
    minMarkup: z.number().nullish(),
    maxMarkup: z.number().nullish(),
    minOverhead: z.number().nullish(),
    maxOverhead: z.number().nullish(),
    minMargin: z.number().nullish(),
    maxMargin: z.number().nullish(),
  }),
);

export const selectForecastSchema = createSelectSchema(schema.forecasts);

export const insertForecastSchema = createInsertSchema(schema.forecasts, {
  contractId: z.string().max(255).optional(),
  ownerId: z.string().uuid().optional().nullable(),
  date: z.coerce.date().optional().nullable(),
  stateId: z.string().uuid().optional().nullable(),
  route: z.string().max(255).optional(),
  description: z.string().optional(),
  scopeNotes: z.string().optional(),
  operationalEfficiency: z.string().optional(),
  riskAndContingency: z.string().optional(),
  backlog: z.string().optional(),
})
  .omit({
    organizationId: true,
    ownerExternalId: true,
  })
  .extend({
    countyIds: z.array(z.string().uuid()).optional(),
    districtIds: z.array(z.string().uuid()).optional(),
    estimatorIds: z.array(z.string().uuid()).optional(),
    workTypeIds: z.array(z.string().uuid()).optional(),
    businessUnitId: z.string().uuid().optional().nullable(),
    markupPercent: z.string().optional().nullable(),
    overheadPercent: z.string().optional().nullable(),
    marginPercent: z.string().optional().nullable(),
    proposalExtensionId: z.string().uuid().optional().nullable(),
  });

export const updateForecastSchema = insertForecastSchema.partial().extend({
  id: z.string().uuid(),
});

/**
 * Strict input schema for **creating** a forecast. Single source of
 * truth for both the UI create form and the `createForecast` agent
 * tool — change validation here and every surface inherits.
 *
 * Required fields enforce minimum viable forecast metadata so the
 * downstream rough-in and prediction flows have everything they need.
 */
export const createForecastInputSchema = insertForecastSchema
  .pick({
    contractId: true,
    ownerId: true,
    date: true,
    stateId: true,
    countyIds: true,
    districtIds: true,
    description: true,
    estimatorIds: true,
    businessUnitId: true,
    workTypeIds: true,
    markupPercent: true,
    overheadPercent: true,
    marginPercent: true,
    proposalExtensionId: true,
    route: true,
    scopeNotes: true,
  })
  .extend({
    contractId: z
      .string()
      .min(1, 'Contract ID is required')
      .max(255)
      .describe('Contract / project identifier (required).'),
    ownerId: z
      .string()
      .uuid('Owner is required')
      .describe(
        'Owner UUID — the DOT or agency issuing the project (required). Use resolveOwner to convert a name into the id; never invent a UUID.',
      ),
    stateId: z
      .string()
      .uuid('State is required')
      .describe(
        "State UUID (required). The resolveOwner tool returns the owner's stateId — reuse it instead of calling another tool.",
      ),
    // Keep as ISO string at the schema layer — Date objects can't be
    // serialized into OpenAI's tool JSON Schema, which breaks the
    // whole tools list silently. Callers coerce to Date right before
    // hitting the service.
    date: z
      .string()
      .date('Letting date is required (YYYY-MM-DD)')
      .describe(
        "Letting date in YYYY-MM-DD format (required). Don't pass timestamps.",
      ),
  });

export type CreateForecastInput = z.infer<typeof createForecastInputSchema>;

export const selectForecastItemSchema = createSelectSchema(
  schema.forecastItems,
);

const numericString = z
  .string()
  .refine((val) => val.trim() !== '', 'Value is required')
  .refine((val) => !Number.isNaN(Number(val)), 'Must be a valid number')
  .refine((val) => {
    const intPart = val.replace(/^-/, '').split('.')[0] ?? '';
    return intPart.length <= 10;
  }, 'Number is too large (max 10 integer digits)');

export const insertForecastItemSchema = createInsertSchema(
  schema.forecastItems,
  {
    lineNumber: z.number().int().positive(),
    itemNumber: z.string().max(255).min(1),
    description: z.string().optional(),
    quantity: numericString,
    unit: z.string().max(50).optional(),
    subPrice: numericString.optional().nullable(),
    subPriceExtension: numericString.optional().nullable(),
  },
).omit({
  organizationId: true,
});

export const selectForecastCompetitorSchema = createSelectSchema(
  schema.forecastCompetitors,
);

export const insertForecastCompetitorSchema = createInsertSchema(
  schema.forecastCompetitors,
  {
    vendorId: z.string().uuid(),
    position: z.number().int().min(1).max(50),
    filters: bidFiltersSchema.nullish(),
  },
).omit({
  organizationId: true,
  vendorExternalId: true,
});

export const selectForecastPostBidEntrySchema = createSelectSchema(
  schema.forecastPostBidEntries,
);

export const insertForecastPostBidEntrySchema = createInsertSchema(
  schema.forecastPostBidEntries,
  {
    vendorName: z.string().max(255).min(1),
    rank: z.number().int().optional(),
    adjustedTotal: z.string().optional(),
    deviation: z.string().optional(),
  },
).omit({
  organizationId: true,
});

export const selectForecastBidAsReadEntrySchema = createSelectSchema(
  schema.forecastBidAsReadEntries,
);

export const insertForecastBidAsReadEntrySchema = createInsertSchema(
  schema.forecastBidAsReadEntries,
  {
    vendorName: z.string().max(255).min(1),
    rank: z.number().int().min(1),
    adjustedTotal: z.string(),
  },
).omit({
  organizationId: true,
  deviation: true,
});

const ForecastsQuerySchema = z.object({
  search: z.string().optional(),
  owner: z.string().optional(),
  counties: z.string().optional(),
  districts: z.string().optional(),
  date: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }
      return val.split(',').map((num) => Number(num));
    })
    .default([]),
  businessUnitIds: z.string().optional(),
  workTypeIds: z.string().optional(),
  estimatorIds: z.string().optional(),
  minMarkup: z.coerce.number().optional(),
  maxMarkup: z.coerce.number().optional(),
  minOverhead: z.coerce.number().optional(),
  maxOverhead: z.coerce.number().optional(),
  minMargin: z.coerce.number().optional(),
  maxMargin: z.coerce.number().optional(),
  hasSimilarBids: z
    .enum(['true', 'false'])
    .transform((val) => val === 'true')
    .optional(),
});

export const getForecastsQuerySchema = createQuerySchemaWithPagination(
  ForecastsQuerySchema,
  { id: 'date', desc: true },
);

export const getForecastsCountQuerySchema = ForecastsQuerySchema;

export const getPostBidItemsQuerySchema = z.object({
  limit: z.coerce.number().optional().default(50),
  offset: z.coerce.number().optional().default(0),
});

/**
 * WHERE ON THE GRID — the one definition, shared by every action and route.
 *
 * TWO AXES, TWO INTERFACES, and which one an operation takes says what it does:
 *
 *   - it prices a COLUMN  → `forecastScopeSchema`   (target [+ competitor])
 *   - it changes the LIST → `type` (rough_in | predict), on its own
 *
 * A price operation does NOT take the list. `listForTarget` derives it from the
 * target, so there is one value to send and no way for the two to disagree —
 * which is what the pair needed a cross-field check to prevent, and that check
 * is what turned these schemas into ZodEffects and 500'd every items request.
 *
 * Eight files each declared their own copy of this enum, thirty references in
 * all, and they had already drifted: some listed three targets, some four, some
 * accepted a competitor for targets that cannot have one.
 *
 * NO `.refine()` HERE, EVER. `@hono/zod-validator` cannot parse a ZodEffects,
 * and these schemas are shared with the routes.
 */
export const forecastScopeSchema = z.object({
  target: z.enum([
    TARGET_ROUGH_IN,
    TARGET_MARKET,
    TARGET_VENDOR,
    TARGET_MY_BID,
  ]),
  /** Only the vendor target has one. The service rejects a vendor without it. */
  competitorId: z.string().nullish(),
});

/**
 * The columns a machine fills. `my_bid` is absent because nothing calculates
 * into it — the user is its sole author — so asking to calculate it is a
 * compile error rather than a runtime refusal.
 */
export const calculableScopeSchema = z.object({
  target: z.enum([TARGET_ROUGH_IN, TARGET_MARKET, TARGET_VENDOR]),
  competitorId: z.string().nullish(),
});

/**
 * THE ONE RULE FOR A LUMP-SUM SHARE: a number in [0, 1). Whoever validates
 * imports this, so there is one answer and one wording — the route and the
 * action had drifted to different messages for the identical input.
 */
export const myLsRatioSchema = z
  .string()
  .regex(/^0(\.\d{1,18})?$/, 'LS % must be at least 0% and less than 100%.')
  .nullable();

const ForecastItemsQuerySchema = z.object({
  itemNumber: z.string().optional(),
  // BOTH COORDINATES, ALWAYS. `type` is the LIST (vertical), `target` is the
  // COLUMN OF PRICES (horizontal), and a row of this table is a cell in that
  // grid. The read used to state only the list and join the rough_in target by
  // hand, so the Prediction list came back carrying the Rough In column's
  // prices no matter which tab asked.
  type: z.enum([ITEM_TYPE_ROUGH_IN, ITEM_TYPE_PREDICT]),
  target: z.enum([
    TARGET_ROUGH_IN,
    TARGET_MARKET,
    TARGET_VENDOR,
    TARGET_MY_BID,
  ]),
  competitorId: z.string().optional(),
});

// A PLAIN OBJECT, deliberately. Cross-field `.refine()`s here turn this into a
// ZodEffects, and the Hono validator this feeds could not parse one — every
// items request 500'd. The pairing they checked is already a compile error on
// the caller: `ForecastGridScope` is a union that only admits a target over the
// list it prices, and only gives `vendor` a competitor.
export const getForecastItemsQuerySchema = createQuerySchemaWithPagination(
  ForecastItemsQuerySchema,
  { id: 'lineNumber', desc: false },
);

const PredictionOutputsQuerySchema = z.object({
  itemNumber: z.string().optional(),
  target: z.string().optional(),
  competitorId: z.string().optional(),
});

export const getPredictionOutputsQuerySchema = createQuerySchemaWithPagination(
  PredictionOutputsQuerySchema,
  { id: 'lineNumber', desc: false },
);
